export type AppMode = 'standalone' | 'restaurant';

export type AppModeSource = 'path' | 'query' | 'none';

export type RestaurantContext = {
  subdomain: string | null;
  tokenId: string | null;
};

export type AppModeState = {
  mode: AppMode;
  source: AppModeSource;
  restaurant: RestaurantContext | null;
};

const safeTrim = (value: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parseSearch = (search: string): URLSearchParams => {
  try {
    return new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  } catch {
    return new URLSearchParams();
  }
};

export function parseAppModeFromPathAndSearch(pathname: string, search: string): AppModeState {
  const segments = pathname.split('/').filter(Boolean);
  const root = segments[0] ?? '';

  if (root === 'restaurant' || root === 'waitlist') {
    const subdomain = safeTrim(segments[1] ?? null);
    const sp = parseSearch(search);
    const tokenId = safeTrim(sp.get('tokenId') ?? sp.get('token'));
    return {
      mode: 'restaurant',
      source: 'path',
      restaurant: { subdomain, tokenId },
    };
  }

  const sp = parseSearch(search);
  const ctx = safeTrim(sp.get('ctx'));
  if (ctx === 'restaurant') {
    const subdomain = safeTrim(sp.get('subdomain') ?? sp.get('sub'));
    const tokenId = safeTrim(sp.get('tokenId') ?? sp.get('token'));
    if (!subdomain && !tokenId) {
      return { mode: 'standalone', source: 'none', restaurant: null };
    }
    return {
      mode: 'restaurant',
      source: 'query',
      restaurant: { subdomain, tokenId },
    };
  }

  return { mode: 'standalone', source: 'none', restaurant: null };
}

/**
 * HashRouter-compatible parser for code that intentionally avoids router deps.
 * Expects strings like "#/play/ludo?ctx=restaurant&subdomain=foo".
 */
export function parseAppModeFromHash(hash: string): AppModeState {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [pathPart, queryPart] = raw.split('?');
  const pathname = pathPart?.startsWith('/') ? pathPart : `/${pathPart ?? ''}`;
  const search = queryPart ? `?${queryPart}` : '';
  return parseAppModeFromPathAndSearch(pathname, search);
}
